// js/AdminBoard/CustomerSupportBoard/DeviceDetail/Tabs/ContactsTab.jsx 
import React, { useEffect, useState } from "react";
import deviceProfile from "images/device.png";
import TrashIcon from "@mui/icons-material/DeleteRounded";
import EditIcon from "@mui/icons-material/EditRounded";
import ContentCopy from "@mui/icons-material/ContentCopy";
import DatePicker from "react-datepicker";
import toast from "react-hot-toast";

import {
    generateDeepLink as generateDeepLinkApi,
    unpairDevice,
    sendInvite,
    generateTemporaryLink,
    fetchOrganizationDevices,
    JoinKonnectDevices,
    UpdateKonnektContact,
    ImportKonnektContacts as ImportKonnektContactsApi,
} from "js/Api/index";
import PaginatedItemsTable from "js/Component/PaginatedItemsTable";
import SegmentedToggle from "js/Component/AddressBook/SegmentedToggle";
import AddressBookView from "js/Component/AddressBook/AddressBookView";
import SharedAddressBooksView from "js/Component/AddressBook/SharedAddressBooksView";

function ContactsTab({
    user,
    fetchLinkedDevices = () => { },
    linkedDevices = [],
    setLinkedDevices = () => { },
    selectedOrg, openEditDevice
}) {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteLink, setInviteLink] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteDevice, setDeleteDevice] = useState(null);
    const [emailAddresses, setEmailAddresses] = useState("");
    const [inviteResults, setInviteResults] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [showTempLinkModal, setShowTempLinkModal] = useState(false);
    const [expiryDate, setExpiryDate] = useState(new Date());
    const [tempLink, setTempLink] = useState("");
    const [showTempLink, setShowTempLink] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isKonnektOrg, setIsKonnektOrg] = useState(false);

    // Address book only applies to CareWindow devices. Detect konnekt by either the
    // selected org (org-user flow) or the device type (customer-support flow where
    // selectedOrg is null).
    const isKonnektContext =
        isKonnektOrg ||
        user?.deviceType === "konnekt-device" ||
        !!user?.konnektDeviceId;

    const [showLinkKonnekt, setShowLinkKonnekt] = useState(false);
    const [konnectDevices, setKonnectDevices] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showKonnectMergeModel, setShowKonnectMergeModel] = useState(false);

    const [showKonnectEditModel, setShowKonnectEditModel] = useState(false);
    const [isManager, setIsManager] = useState(false);
    const [editId, setEditId] = useState();
    const [updateShortName, setUpdateShortName] = useState("");

    const [addressBookMode, setAddressBookMode] = useState("legacy");

    useEffect(() => {
        if (!user?.id) return;

        fetchLinkedDevices();
        getDeepLink();
    }, [user?.id]);

    useEffect(() => {
        if (selectedOrg?.code === "konnekt") {
            setIsKonnektOrg(true);
            fetchKonnektDevices();
        } else {
            setIsKonnektOrg(false);
        }
    }, [selectedOrg?.code]);

    const getDeepLink = async () => {
        if (!user?.id) {
            console.warn("Cannot generate deep link: user.id is undefined");
            return;
        }

        const result = await generateDeepLinkApi(user.id);

        if (result.success) {
            setInviteLink(result.data.url);
        } else {
            console.log("Failed to generate deep link:", result.error);
        }
    };

    const handleUnpairDevice = async (deviceId) => {
        const result = await unpairDevice(deviceId);

        if (result.success) {
            setLinkedDevices(linkedDevices.filter((d) => d.id !== deviceId));
            toast.success(result.message);
            fetchLinkedDevices();
        } else {
            console.log("Failed to unpair device:", result.error);
            toast.error(result.error);
        }
    };

    const handleDeleteDeviceClick = (device) => {
        setDeleteDevice(device);
        setShowDeleteModal(true);
    };

    const handleDeleteDeviceCancel = () => {
        setShowDeleteModal(false);
        setDeleteDevice(null);
    };

    const handleUnpairClick = () => {
        if (deleteDevice) {
            handleUnpairDevice(deleteDevice.id);
            setShowDeleteModal(false);
            setDeleteDevice(null);
        }
    };

    const handleInviteModal = () => {
        setShowInviteModal(true);
    };

    const handleSendClick = async (event) => {
        event.preventDefault();
        setIsSending(true);
        setInviteResults([]);

        const emailAddressArray = emailAddresses
            .split(/[,;\n]/)
            .map((email) => email.trim())
            .filter((email) => email !== "");

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        for (const email of emailAddressArray) {
            if (emailRegex.test(email)) {
                const result = await sendInvite(user.id, email, inviteLink);

                setInviteResults((prevResults) => [
                    ...prevResults,
                    {
                        email,
                        success: result.success,
                        message: result.success ? result.message : result.error,
                    },
                ]);
            } else {
                setInviteResults((prevResults) => [
                    ...prevResults,
                    { email, success: false, message: "Invalid email address" },
                ]);
            }
        }

        setIsSending(false);
    };

    const handleCopyInviteLink = () => {
        const orgName = selectedOrg?.name || "CareWindow";
        const text =
            orgName +
            ` has invited you to join ${user.name}'s CareWindow family group. ` +
            `Tap on this link on your smartphone or tablet to download the CareWindow app and join the group.\n${inviteLink}`;

        navigator.clipboard.writeText(text);
        toast.success("Invite link copied to clipboard!");
    };

    const toggleDropdown = () => {
        setIsDropdownOpen((v) => !v);
    };

    const handleTempLinkModal = () => {
        setShowTempLinkModal(true);
        setShowTempLink(false);
        setTempLink("");
    };

    const handleGenerateLink = async () => {
        const result = await generateTemporaryLink(
            user.id,
            expiryDate.toISOString()
        );

        if (result.success) {
            setTempLink(result.data.url);
            setShowTempLink(true);
            toast.success("Temporary link generated successfully!");
        } else {
            toast.error(
                result.error || "Failed to generate temporary link. Please try again."
            );
        }
    };

    const handleCopyTempLink = () => {
        navigator.clipboard.writeText(tempLink);
        toast.success("Temporary link copied to clipboard!");
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const fetchKonnektDevices = async () => {
        if (!selectedOrg?.code) return;
        const response = await fetchOrganizationDevices(selectedOrg.code);
        if (response.success) {
            setKonnectDevices(response.data.filter((a) => a.id !== user?.id));
        }
    };

    const handleKonnektDeviceAdd = () => {
        setShowLinkKonnekt(true);
        setSelectedItems([]);
        setSearchQuery("");
    };

    const handleCancelLinkKonnekt = () => {
        setSelectedItems([]);
        setShowLinkKonnekt(false);
        setSearchQuery("");
    };

    const filteredItems = konnectDevices.filter((item) => {
        const q = searchQuery.toLowerCase();
        return (
            item.name.toLowerCase().includes(q) ||
            (item.serialNo && item.serialNo.toLowerCase().includes(q))
        );
    });

    const filteredLinkedDevices = (() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return linkedDevices;
        return linkedDevices.filter((d) => {
            return (
                (d.name || "").toLowerCase().includes(q) ||
                (d.shortName || "").toLowerCase().includes(q) ||
                (d.accountId || "").toLowerCase().includes(q) ||
                (d.serialNo || "").toLowerCase().includes(q)
            );
        });
    })();

    const handleItemSelect = (itemId) => {
        setSelectedItems((prev) =>
            prev.includes(itemId)
                ? prev.filter((id) => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handleConfirmMerge = () => {
        setShowKonnectMergeModel(true);
    };

    const handleGroupKonnktIdConfirm = async () => {
        const requestBody = {
            id: user.id,
            devices: selectedItems,
        };

        const result = await JoinKonnectDevices(requestBody);
        if (result.success) {
            toast.success("Konnekt devices merged to family group.");
            window.location.reload();
        } else {
            console.log("Failed to merge family");
            toast.error(result.error);
        }
    };

    const handleImportKonnektContacts = async () => {
        const result = await ImportKonnektContactsApi(user.id);
        if (result.success) {
            toast.success("Contacts imported successfully!");
            fetchLinkedDevices();
        } else {
            toast.error(result.error || "Failed to import contacts.");
        }
    };

    const handleEditKonnectContact = (id) => {
        const item = linkedDevices.find((d) => d.id === id);
        if (!item) return;
        setUpdateShortName(item.shortName || "");
        setEditId(id);
        setIsManager(item.isLeader || false);
        setShowKonnectEditModel(true);
    };

    const handleUpdateContact = async () => {
        const requestBody = {
            id: editId,
            shortName: updateShortName,
            manager: isManager,
        };

        const result = await UpdateKonnektContact(requestBody);
        if (result.success) {
            toast.success("Contact updated.");
            fetchLinkedDevices();
            setShowKonnectEditModel(false);
        } else {
            console.log("Failed to update contact.");
            toast.error(result.error);
        }
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
    };

    const contactsToolbar = (
        <>
            {isKonnektContext ? (
                <div style={{ marginBottom: 20 }}>
                    <button
                        onClick={handleKonnektDeviceAdd}
                        type="button"
                        className="btn cur-p btn-outline-dark"
                        style={{ marginRight: 10 }}
                    >
                        Add Konnekt Device
                    </button>
                    <button
                        onClick={handleTempLinkModal}
                        type="button"
                        className="btn cur-p btn-outline-dark"
                        style={{ marginRight: 10 }}
                    >
                        Share Temporary Link
                    </button>
                    <button
                        onClick={handleImportKonnektContacts}
                        type="button"
                        className="btn cur-p btn-outline-dark"
                    >
                        Import Konnekt Contacts
                    </button>
                </div>
            ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <div
                        className="dropdown"
                        style={{ marginBottom: 20, position: "relative" }}
                    >
                        <button
                            className={`btn btn-dark dropdown-toggle ${isDropdownOpen ? "show" : ""
                                }`}
                            type="button"
                            onClick={toggleDropdown}
                            aria-expanded={isDropdownOpen}
                            style={{ fontWeight: 700, width: "15rem" }}
                        >
                            Invite Family Members
                        </button>

                        {isDropdownOpen && (
                            <ul
                                className="dropdown-menu"
                                style={{
                                    display: "block",
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 1000,
                                    backgroundColor: "#fff",
                                    border: "1px solid rgba(0,0,0,.15)",
                                    borderRadius: "0.25rem",
                                    padding: "0.5rem 0",
                                    minWidth: "10rem",
                                }}
                            >
                                <li>
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            handleInviteModal();
                                            setIsDropdownOpen(false);
                                        }}
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            padding: "0.25rem 1rem",
                                            clear: "both",
                                            fontWeight: 400,
                                            textAlign: "left",
                                            border: 0,
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                        }}
                                    >
                                        By Email
                                    </button>
                                </li>
                                <li>
                                    <button
                                        className="dropdown-item"
                                        onClick={() => {
                                            handleCopyInviteLink();
                                            setIsDropdownOpen(false);
                                        }}
                                        style={{
                                            display: "block",
                                            width: "100%",
                                            padding: "0.25rem 1rem",
                                            clear: "both",
                                            fontWeight: 400,
                                            textAlign: "left",
                                            border: 0,
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Copy invitation to clipboard
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>

                    <button
                        onClick={handleTempLinkModal}
                        type="button"
                        className="btn cur-p btn-outline-dark"
                        style={{ marginBottom: 20 }}
                    >
                        Share Temporary Link
                    </button>
                </div>
            )}

            <div
                className="form-group"
                style={{ marginBottom: 20, maxWidth: "50%" }}
            >
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search contacts by name..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                    />
                </div>
            </div>
        </>
    );

    const legacyTable = (
        <>
            <div style={{ overflowX: "auto" }}>
                <table className="table table-hover">
                    <caption
                        style={{
                            captionSide: "top",
                            textAlign: "center",
                            marginRight: 160,
                        }}
                    >
                        <h3>Contacts</h3>
                    </caption>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Contact</th>
                            <th>Account ID</th>
                            <th>Short Name</th>
                            <th>Konnekt / Skype Id</th>
                            <th>Manager</th>
                            <th>Primary</th>
                            <th>User Email</th>
                            <th>Recovery Email</th>
                            <th>Email Verified</th>
                            <th>Device Type</th>
                            <th>Serial No</th>
                            <th>Device Status</th>
                            <th>App Version</th>
                            <th>Org Code</th>
                            <th>Org Enroll Date</th>
                            <th>Preferred Lang</th>
                            <th>Time Zone</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                            <th>Master Device Id</th>
                            <th>EveryoungAI Project Id</th>
                            <th>FCM Token</th>
                            <th>Pushy Token</th>
                            <th>VOIP Token</th>
                            <th>Agora UID</th>
                            <th>Translate Behaviour</th>
                            <th>Case Notes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLinkedDevices.map((device) => (
                            <tr key={device.id}>
                                {/* ID */}
                                <td
                                    style={{
                                        verticalAlign: "middle",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {device.id}
                                    {device.id && (
                                        <ContentCopy
                                            className="iconButtonStyle"
                                            sx={{ marginLeft: "5px", cursor: "pointer" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(device.id);
                                                toast.success("Copied");
                                            }}
                                        />
                                    )}
                                </td>

                                {/* Contact: avatar + name */}
                                <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                    {device.profilePicture ? (
                                        <img
                                            className="user-photo"
                                            src={`data:image/png;base64, ${device.profilePicture}`}
                                            alt={device.name}
                                        />
                                    ) : (
                                        <img
                                            className="user-photo"
                                            src={deviceProfile}
                                            alt="Default profile"
                                        />
                                    )}
                                    {device.name}
                                </td>

                                {/* Account ID */}
                                <td
                                    style={{
                                        verticalAlign: "middle",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {device.accountId || "-"}
                                    {device.accountId && (
                                        <ContentCopy
                                            className="iconButtonStyle"
                                            sx={{ marginLeft: "5px", cursor: "pointer" }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(device.accountId);
                                                toast.success("Copied");
                                            }}
                                        />
                                    )}
                                </td>

                                {/* Short Name（konnekt 才有，其他 org 为空也没关系） */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.shortName || "-"}
                                </td>

                                {/* Konnekt / Skype Id */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.konnektDeviceId || "-"}
                                </td>

                                {/* Manager */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.isLeader ? "Manager" : "Normal user"}
                                </td>

                                {/* Primary */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.isPrimary ? "Primary" : "-"}
                                </td>

                                {/* User Email */}
                                <td style={{ verticalAlign: "middle", fontSize: "12px" }}>
                                    {device.userEmail || "-"}
                                </td>

                                {/* Recovery Email */}
                                <td style={{ verticalAlign: "middle", fontSize: "12px" }}>
                                    {device.recoveryEmail || "-"}
                                </td>

                                {/* Email Verified */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.isEmailVerified == null
                                        ? "-"
                                        : device.isEmailVerified
                                            ? "Yes"
                                            : "No"}
                                </td>

                                {/* Device Type */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.deviceType || "-"}
                                </td>

                                {/* Serial No */}
                                <td
                                    style={{
                                        verticalAlign: "middle",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {device.serialNo || "-"}
                                </td>

                                {/* Device Status */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.deviceStatus || "-"}
                                </td>

                                {/* App Version */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.apP_Version || "-"}
                                </td>

                                {/* Org Code */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.orgCode || "-"}
                                </td>

                                {/* Org Enroll Date */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {formatDate(device.orgEnrollDate)}
                                </td>

                                {/* Preferred Lang */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.preferredLang || "-"}
                                </td>

                                {/* Time Zone */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.timeZone
                                        ? typeof device.timeZone === "string"
                                            ? device.timeZone
                                            : JSON.stringify(device.timeZone)
                                        : "-"}
                                </td>

                                {/* Created At */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {formatDate(device.createdAt)}
                                </td>

                                {/* Updated At */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {formatDate(device.updatedAt)}
                                </td>

                                {/* Master Device Id */}
                                <td
                                    style={{
                                        verticalAlign: "middle",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {device.masterDeviceId || "-"}
                                </td>

                                {/* EveryoungAI Project Id */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.everyoungaiProjectId || "-"}
                                </td>

                                {/* FCM Token */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.fcmToken || "-"}
                                </td>

                                {/* Pushy Token */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.pushyToken || "-"}
                                </td>

                                {/* VOIP Token */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.voipToken || "-"}
                                </td>

                                {/* Agora UID */}
                                <td style={{ verticalAlign: "middle" }}>
                                    {device.agoraUid ?? "-"}
                                </td>

                                {/* Translate Behaviour */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.translateBehaviour || "-"}
                                </td>

                                {/* Case Notes */}
                                <td style={{ verticalAlign: "middle", fontSize: "11px" }}>
                                    {device.caseNotes || "-"}
                                </td>

                                {/* Actions */}
                                <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                    <EditIcon
                                        onClick={() => openEditDevice(device)}
                                        className="iconButtonStyle"
                                        sx={{ marginRight: "5px" }}
                                    />
                                    <TrashIcon
                                        className="iconButtonStyle"
                                        onClick={() => handleDeleteDeviceClick(device)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );

    const legacyModals = (
        <>
            {showDeleteModal && (
                <div
                    className="modal fade show"
                    style={{ display: "block" }}
                    aria-modal="true"
                    role="dialog"
                >
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h1 className="modal-title">Delete Device</h1>
                                <button
                                    type="button"
                                    className="close-button"
                                    onClick={handleDeleteDeviceCancel}
                                    aria-label="Close"
                                >
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to unenroll{" "}
                                    <strong>{deleteDevice?.name}</strong>?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleDeleteDeviceCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleUnpairClick}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showInviteModal && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 400 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Invite Contacts</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowInviteModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <form className="contact-form" onSubmit={handleSendClick}>
                                <div className="mb-3">
                                    <p>You may enter up to 6 email addresses, one per line.</p>
                                    <strong>
                                        <label
                                            htmlFor="ContactsEmailAddress"
                                            className="form-label"
                                            style={{ marginTop: 20 }}
                                        >
                                            Email Addresses
                                        </label>
                                    </strong>
                                    <textarea
                                        className="form-control"
                                        id="ContactsEmailAddress"
                                        rows="8"
                                        placeholder={`joe.baker@example.com\nfran.baker@example.com`}
                                        value={emailAddresses}
                                        onChange={(e) => setEmailAddresses(e.target.value)}
                                    />
                                </div>
                                {inviteResults.length > 0 && (
                                    <div className="mb-3">
                                        <h5>Invite Results:</h5>
                                        <ul>
                                            {inviteResults.map((result, index) => (
                                                <li
                                                    key={index}
                                                    className={
                                                        result.success ? "text-success" : "text-danger"
                                                    }
                                                >
                                                    {result.email}: {result.message}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn cur-p btn-secondary"
                                        onClick={() => setShowInviteModal(false)}
                                        disabled={isSending}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn cur-p btn-primary"
                                        disabled={isSending}
                                    >
                                        {isSending ? "Sending..." : "Send"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {showTempLinkModal && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 500 }}>
                            <div className="modal-header">
                                <h1 className="modal-title" style={{ fontWeight: 700 }}>
                                    Share Temporary Link
                                </h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowTempLinkModal(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p style={{ marginBottom: 30, color: "black" }}>
                                    Share this link with a doctor, nurse, carer or other trusted
                                    person to allow them call {user.name}'s CareWindow device from
                                    their web browser.
                                </p>
                                <div className="form-group" style={{ marginBottom: 30 }}>
                                    <label
                                        htmlFor="expiryDate"
                                        style={{
                                            display: "block",
                                            marginBottom: 10,
                                            fontWeight: 400,
                                            color: "black",
                                        }}
                                    >
                                        Expiry date
                                    </label>
                                    <DatePicker
                                        selected={expiryDate}
                                        onChange={(date) => setExpiryDate(date)}
                                        className="form-control"
                                        dateFormat="dd/MM/yyyy"
                                        minDate={new Date()}
                                    />
                                </div>
                                <button
                                    className="btn btn-dark"
                                    onClick={handleGenerateLink}
                                    style={{ marginBottom: 30, width: 150 }}
                                >
                                    Generate Link
                                </button>

                                {showTempLink && (
                                    <div className="form-group">
                                        <label
                                            htmlFor="tempLink"
                                            style={{
                                                display: "block",
                                                marginBottom: 10,
                                                fontWeight: 400,
                                                color: "black",
                                            }}
                                        >
                                            Link
                                        </label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={tempLink}
                                                readOnly
                                                style={{ marginRight: 10 }}
                                            />
                                            <button
                                                className="btn btn-outline-dark"
                                                type="button"
                                                onClick={handleCopyTempLink}
                                                style={{ width: 100 }}
                                            >
                                                Copy
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showLinkKonnekt && (
                <div
                    className="modal"
                    tabIndex="-1"
                    role="dialog"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog" role="document">
                        <div
                            className="modal-content"
                            style={{ height: "98vh", minWidth: 700 }}
                        >
                            <div className="modal-header">
                                <h1 className="modal-title">
                                    Add another Konnekt to family group
                                </h1>
                                <button
                                    className="close-button"
                                    onClick={handleCancelLinkKonnekt}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by CareWindow User or Serial"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>

                                <PaginatedItemsTable
                                    items={filteredItems}
                                    selectedItems={selectedItems}
                                    onItemSelect={handleItemSelect}
                                    itemType="device"
                                    itemsPerPage={10}
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCancelLinkKonnekt}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleConfirmMerge}
                                    disabled={selectedItems.length === 0}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showKonnectMergeModel && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 400 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Merge Konnekt Devices</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowKonnectMergeModel(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to merge selected devices to single
                                    family group?
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowKonnectMergeModel(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleGroupKonnktIdConfirm}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showKonnectEditModel && (
                <div className="modal" role="dialog">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content" style={{ width: 400 }}>
                            <div className="modal-header">
                                <h1 className="modal-title">Edit Contact</h1>
                                <button
                                    className="close-button"
                                    onClick={() => setShowKonnectEditModel(false)}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="col-form-label">Short Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        maxLength="50"
                                        required
                                        value={updateShortName}
                                        onChange={(e) => setUpdateShortName(e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="radio"
                                            name="userType"
                                            id="Family_manager"
                                            value="Family_manager"
                                            checked={isManager}
                                            onChange={(e) => setIsManager(e.target.checked)}
                                        />
                                        &nbsp; Family manager
                                        &nbsp;
                                        <input
                                            type="radio"
                                            name="userType"
                                            id="normal_user"
                                            value="normal_user"
                                            checked={isManager === false}
                                            onChange={(e) => setIsManager(!e.target.checked)}
                                        />
                                        &nbsp; Normal user
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn cur-p btn-secondary"
                                    onClick={() => setShowKonnectEditModel(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn cur-p btn-primary"
                                    onClick={handleUpdateContact}
                                >
                                    Update
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <div>
            {contactsToolbar}
            {!isKonnektContext && (
                <div style={{ marginBottom: 16 }}>
                    <SegmentedToggle
                        options={[
                            { value: "legacy", label: "Old Contacts" },
                            { value: "home", label: "Home Page Contacts" },
                            { value: "shared", label: "Shared Address Books" },
                        ]}
                        value={addressBookMode}
                        onChange={setAddressBookMode}
                    />
                </div>
            )}
            {(isKonnektContext || addressBookMode === "legacy") && legacyTable}
            {!isKonnektContext && addressBookMode === "home" && (
                <AddressBookView
                    deviceId={user?.id}
                    canEdit
                    verbose
                    searchQuery={searchQuery}
                />
            )}
            {!isKonnektContext && addressBookMode === "shared" && (
                <SharedAddressBooksView
                    deviceId={user?.id}
                    searchQuery={searchQuery}
                />
            )}
            {legacyModals}
        </div>
    );
}

export default ContactsTab;
